import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { User, createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly client: SupabaseClient | null = this.createClient();
  private readonly initialization: Promise<void>;

  readonly user = signal<User | null>(null);
  readonly initialized = signal(false);
  readonly error = signal<string | null>(null);
  readonly isConfigured = Boolean(environment.supabaseUrl && environment.supabasePublishableKey);

  /** Used only by services that make authenticated, user-scoped Supabase calls. */
  get supabase(): SupabaseClient | null {
    return this.client;
  }

  constructor() {
    // Field initializers run in declaration order. Start asynchronously only
    // after the signals above have been created.
    this.initialization = this.initialize();
    this.destroyRef.onDestroy(() => this.client?.auth.stopAutoRefresh());
  }

  async waitUntilInitialized(): Promise<void> {
    await this.initialization;
  }

  async signInWithGoogle(returnUrl?: string | null): Promise<void> {
    if (!this.client) {
      this.error.set('Supabase is not configured for this environment.');
      return;
    }

    this.error.set(null);
    const callbackUrl = new URL('/auth/callback', window.location.origin);
    if (returnUrl?.startsWith('/')) callbackUrl.searchParams.set('returnUrl', returnUrl);

    const { error } = await this.client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: callbackUrl.toString() },
    });
    if (error) this.error.set(error.message);
  }

  async signOut(): Promise<void> {
    if (!this.client) return;
    const { error } = await this.client.auth.signOut();
    if (error) this.error.set(error.message);
  }

  async accessToken(): Promise<string | null> {
    if (!this.client) return null;
    const { data } = await this.client.auth.getSession();
    return data.session?.access_token ?? null;
  }

  functionUrl(name: string): string | null {
    if (!environment.supabaseUrl) return null;
    return `${environment.supabaseUrl.replace(/\/+$/, '')}/functions/v1/${name}`;
  }

  private createClient(): SupabaseClient | null {
    if (!environment.supabaseUrl || !environment.supabasePublishableKey) return null;
    return createClient(environment.supabaseUrl, environment.supabasePublishableKey, {
      auth: {
        flowType: 'pkce',
        detectSessionInUrl: true,
      },
    });
  }

  private async initialize(): Promise<void> {
    if (!this.client) {
      this.initialized.set(true);
      return;
    }

    const { data, error } = await this.client.auth.getSession();
    if (error) this.error.set(error.message);
    this.user.set(data.session?.user ?? null);
    this.initialized.set(true);

    this.client.auth.onAuthStateChange((_event, session) => {
      this.user.set(session?.user ?? null);
      this.initialized.set(true);
    });
  }
}
